/**
 * スタミナ残量とデイリーボーナス UI。
 *
 * - ハート残量（赤）＋消費済み（灰）＋上限数値 を横並びで表示。
 * - 残 0 のときは次回回復までの時間とリワード回復ボタンを出す。
 * - デイリーボーナスが未取得ならリワード視聴ボタンを添える。
 * - LINE Mini App では Yahoo 広告、ネイティブでは AdMob / mock 経路へ `services/ads.ts` が分岐する。
 *
 * 参考ルール: `.claude/rules/no-hardcode.md`
 *   - 最大スタミナは useStamina() 側から取得してそのまま描画する
 *     （ここに MAX=5 などをハードコードしない）。
 */
import { useStamina } from '../hooks/useStamina'
import { t, tx } from '../i18n/translations'

interface Props {
  /** 残 0 時に回復ボタンを表示するか。タイトル画面のみ true を想定。 */
  showRecoverButton?: boolean
  /** 広告経由でフル回復した後に呼ばれるコールバック（任意）。 */
  onRecovered?: () => void
  /** リワード視聴後にデイリーボーナスを受け取ったら呼ばれるコールバック（任意）。 */
  onDailyBonusClaimed?: () => void
}

/** ハート 1 個分の表示用要素。 */
function HeartIcon({ filled }: { filled: boolean }) {
  return (
    <span
      className={`stamina-display__heart ${
        filled ? 'stamina-display__heart--filled' : 'stamina-display__heart--empty'
      }`}
      aria-hidden="true"
    >
      {filled ? '❤' : '♡'}
    </span>
  )
}

export function StaminaDisplay({
  showRecoverButton = false,
  onRecovered,
  onDailyBonusClaimed,
}: Props) {
  const {
    stamina,
    maxStamina,
    nextRecoveryLabel,
    isRecovering,
    isClaimingDailyBonus,
    canClaimDailyBonus,
    recoverFullViaAd,
    claimDailyBonusViaAd,
  } = useStamina()

  const hearts = Array.from({ length: maxStamina }, (_, i) => i < stamina)

  const handleRecover = async () => {
    const ok = await recoverFullViaAd()
    if (ok) onRecovered?.()
  }

  const handleDailyBonus = async () => {
    const ok = await claimDailyBonusViaAd()
    if (ok) onDailyBonusClaimed?.()
  }

  return (
    <div className="stamina-display">
      <div
        className="stamina-display__row"
        role="status"
        aria-label={tx('staminaAria', { stamina, max: maxStamina })}
      >
        <div className="stamina-display__hearts">
          {hearts.map((filled, i) => (
            <HeartIcon key={i} filled={filled} />
          ))}
        </div>
        <span className="stamina-display__count">
          {stamina}/{maxStamina}
        </span>
      </div>

      {stamina < maxStamina && nextRecoveryLabel && (
        <div className="stamina-display__recovery">
          {tx('nextRecovery', { time: nextRecoveryLabel })}
        </div>
      )}

      {showRecoverButton && stamina === 0 && (
        <button
          type="button"
          className="btn btn--secondary stamina-display__recover-btn"
          onClick={handleRecover}
          disabled={isRecovering}
        >
          {isRecovering ? t('adPlaying') : t('recoverFullAd')}
        </button>
      )}

      {canClaimDailyBonus && (
        <button
          type="button"
          className="stamina-display__daily-bonus"
          onClick={handleDailyBonus}
          disabled={isClaimingDailyBonus}
          aria-label={t('dailyBonusAria')}
        >
          <span aria-hidden="true">🎁</span>
          <span className="stamina-display__daily-bonus-label">
            {isClaimingDailyBonus ? t('adPlaying') : t('dailyBonusAd')}
          </span>
        </button>
      )}
    </div>
  )
}
